import React, { lazy } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

// This component loads 3 HUGE different components. They are split between different chunks.
// They need to maintain the same chunk name hint for webppack between here and routes/index.tsx.
// If those names get out of sync I am not sure of the consequences.
import Loading from '../../components/Loading';
import {
  CONVOV2_LK_TRANSCRIPT_ROUTE,
  getLKTranscriptSearch,
  hasLegacyInviteToken,
} from '../../modules/convoV2/constants';
import { ConversationVersionMismatchDialog } from '../../modules/convoV2/ConversationVersionMismatchDialog';
const Default = lazy(() => import(/* webpackChunkName: "default" */ '../../Layouts/Default'));
import { selectConfigEnableConvoV2 } from '../../selectors/auth';
import { selectLoggedIn, selectLoggedInAnonymously } from '../../selectors/combined';
import { selectIsInConversation } from '../../selectors/conversation';
import { selectFeature, selectFeatures, selectShouldUseLiveKitWebFlow } from '../../selectors/userProfile';
import { selectV1Token } from '../../selectors/v1Session';
import { useAppSelector } from '../../store/store';

const ConvoV2ConversationPage = lazy(
  () => import(/* webpackChunkName: "convoV2" */ '../../modules/convoV2/ConvoV2ConversationPage')
);
const ConversationPage = lazy(
  () => import(/* webpackChunkName: "convoV1" */ '../../views/conversation/ConversationPage/ConversationPage')
);

export const ConvoV2ConversationPageSwitch = React.memo(() => {
  const location = useLocation();
  const features = useAppSelector(selectFeatures);
  const hasFlagInUserProfile = features['convo-v2'];
  const hasFlagInFirebase = useAppSelector(selectConfigEnableConvoV2);
  const isScribe = useAppSelector(selectFeature('scribe'));
  const shouldUseLiveKitWebFlow = useAppSelector(selectShouldUseLiveKitWebFlow);
  const isLoggedIn = useAppSelector(selectLoggedIn);
  const isLoggedInAnonymously = Boolean(useAppSelector(selectLoggedInAnonymously));
  const isInConversation = useAppSelector(selectIsInConversation);
  const v1Token = useAppSelector(selectV1Token);
  const hasPendingLegacyAvaRequest = Boolean(localStorage.getItem('ava_request'));
  // Detect a legacy invite from the redux v1Token, not just location.search. v1Session keeps
  // the URL token in sync via history.replaceState, which never reaches react-router's location.
  const hasLegacyToken = Boolean(v1Token) || hasLegacyInviteToken(location.search);
  const hasLegacyInvite = hasLegacyToken || hasPendingLegacyAvaRequest;
  const hasLegacyMismatch = isLoggedIn && shouldUseLiveKitWebFlow && hasLegacyInvite;
  const canRedirectToLiveKit =
    shouldUseLiveKitWebFlow && !isLoggedInAnonymously && !hasLegacyInvite && !isInConversation;

  if (hasLegacyMismatch) {
    return <ConversationVersionMismatchDialog requestedVersion="legacy" />;
  }

  if (canRedirectToLiveKit) {
    return (
      <Navigate
        to={{
          pathname: CONVOV2_LK_TRANSCRIPT_ROUTE,
          search: getLKTranscriptSearch(location.search),
        }}
        replace
      />
    );
  }

  // Scribes always get ConvoV1 until we implement scribing in ConvoV2
  if (!isScribe) {
    if (hasFlagInUserProfile || (hasFlagInFirebase && hasFlagInUserProfile !== false)) {
      return <ConvoV2ConversationPage />;
    }
    if (hasFlagInFirebase === undefined) {
      return <Loading />;
    }
  }

  return (
    <Default>
      <ConversationPage />
    </Default>
  );
});
